
import {
  Vue, Component, Prop, Watch, Mixins,
} from 'vue-property-decorator';
import moment from 'moment';
import API from '@/lib/API';
import { Lead, RuleTestResponse } from '@/types';
import DatePickerDialog from '@/components/DatePickerDialog.vue';
import UtilsMixin from '@/mixins/UtilsMixin.vue';

moment.locale('it');

@Component({
  components: {
    DatePickerDialog,
  },
})
export default class LeadRuleTesterDialog extends Mixins(UtilsMixin) {
  @Prop(Boolean) readonly value!: boolean;

  dialog = false
  loading = false
  simulatedDate = ''
  testResults: RuleTestResponse | null = null
  error: string | null = null
  datePickerDialog = false
  datePickerDate: string | null = null
  copiedRuleId: number | null = null
  copiedRuleIdTimeout: ReturnType<typeof setTimeout> | null = null

  @Watch('value')
  onValueChange(val: boolean) {
    this.dialog = val;
    if (val) {
      // Inizializza la data simulata a oggi quando si apre il dialog
      const today = new Date();
      this.simulatedDate = today.toISOString().split('T')[0];
      this.testResults = null;
      this.error = null;
    }
  }

  get simulatedDateAsDate(): Date | null {
    return this.simulatedDate ? moment(this.simulatedDate, 'YYYY-MM-DD').toDate() : null;
  }

  @Watch('dialog')
  onDialogChange(val: boolean) {
    if (this.value !== val) {
      this.$emit('input', val);
    }
  }

  get currentLead(): Lead | null {
    return this.$store.getters.currentLead;
  }

  get totalRules(): number {
    return this.testResults?.rules.length || 0;
  }

  get satisfiedRules(): number {
    return this.testResults?.rules.filter((r) => r.triggered).length || 0;
  }

  get notSatisfiedRules(): number {
    return this.testResults?.rules.filter((r) => !r.triggered).length || 0;
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  async onTestRules() {
    console.log('LeadRuleTesterDialog: onTestRules()'); // eslint-disable-line no-console

    if (!this.currentLead) {
      this.error = 'Nessuna lead selezionata';
      return;
    }

    this.loading = true;
    this.error = null;
    this.testResults = null;

    try {
      const response = await API.testRules(this.currentLead.id, this.simulatedDate);
      this.testResults = response;
    } catch (err) {
      console.error('Errore durante il test delle regole:', err); // eslint-disable-line no-console
      const error = err as any;
      this.error = error.response?.data?.error || error.message || 'Errore durante il test delle regole';
    } finally {
      this.loading = false;
    }
  }

  onClose() {
    console.log('LeadRuleTesterDialog: onClose()'); // eslint-disable-line no-console

    this.dialog = false;
    this.testResults = null;
    this.error = null;
  }

  onClickDate() {
    console.log('LeadRuleTesterDialog: onClickDate()'); // eslint-disable-line no-console

    // Imposta l'eventuale data precedentemente selezionata
    this.datePickerDate = this.simulatedDate || null;

    // Apre il dialog per la selezione della data
    this.datePickerDialog = true;
  }

  onSelectDate(date: string | null) {
    console.log('LeadRuleTesterDialog: onSelectDate() date =', date); // eslint-disable-line no-console

    // Chiudo il dialog per la selezione della data
    this.datePickerDialog = false;

    // Aggiorna la data simulata
    this.simulatedDate = date || '';
  }

  onClearDate() {
    console.log('LeadRuleTesterDialog: onClearDate()'); // eslint-disable-line no-console

    // Cancella la data simulata
    this.simulatedDate = '';
  }

  async copyRuleIdToClipboard(ruleId: number) {
    const text = String(ruleId);
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      if (this.copiedRuleIdTimeout) clearTimeout(this.copiedRuleIdTimeout);
      this.copiedRuleId = ruleId;
      this.copiedRuleIdTimeout = setTimeout(() => {
        this.copiedRuleId = null;
        this.copiedRuleIdTimeout = null;
      }, 2000);
    } catch (err) {
      console.error('Errore durante la copia:', err); // eslint-disable-line no-console
    }
  }

  formatConditionValues(customValues?: Array<import('@/types').RuleTestConditionCustomValue>): string {
    if (!customValues || customValues.length === 0) {
      return '';
    }

    const formatSingleValue = (customValue: import('@/types').RuleTestConditionCustomValue): string => {
      let formattedValue = '';

      switch (customValue.type) {
        case 'text':
        case 'task':
          formattedValue = customValue.value as string || '';
          break;

        case 'status': {
          let statusValues: string[] = [];
          if (Array.isArray(customValue.value)) {
            statusValues = customValue.value;
          } else if (typeof customValue.value === 'string') {
            try {
              statusValues = JSON.parse(customValue.value);
            } catch {
              statusValues = [];
            }
          }
          formattedValue = this.formatStatusString(statusValues);
          break;
        }

        case 'activityStatus': {
          let activityValues: string[] = [];
          if (Array.isArray(customValue.value)) {
            activityValues = customValue.value;
          } else if (typeof customValue.value === 'string') {
            try {
              activityValues = JSON.parse(customValue.value);
            } catch {
              activityValues = [];
            }
          }
          formattedValue = this.formatActivityStatusString(activityValues);
          break;
        }

        case 'egridConnectionStatus':
          formattedValue = customValue.value as string || '';
          break;

        case 'commissioningStatus':
          formattedValue = this.formatCommissioningStatusString(customValue.value as string);
          break;

        case 'assignmentType': {
          const assignmentTypeLabels: Record<string, string> = {
            practice: 'Pratiche di connessione',
            project: 'Progetto',
            dogane: 'Dogane',
            enea: 'ENEA/GSE/Altro',
          };
          formattedValue = assignmentTypeLabels[customValue.value as string] || (customValue.value as string) || '';
          break;
        }

        case 'category':
        case 'tag':
          formattedValue = customValue.categoryValue?.name || customValue.tagValue?.name || '';
          break;

        case 'user':
          formattedValue = customValue.userValue?.name || '';
          break;

        case 'group':
          if (customValue.multiple && Array.isArray(customValue.value)) {
            // Quando multiple è true, value contiene un array di gruppi
            formattedValue = (customValue.value as any[])
              .map((g: any) => (typeof g === 'object' && g.name ? g.name : String(g)))
              .join(', ');
          } else {
            formattedValue = customValue.groupValue?.name || '';
          }
          break;

        case 'boolean':
          formattedValue = customValue.booleanValue ? 'Sì' : 'No';
          break;

        case 'date':
          if (customValue.dateValue) {
            const date = moment(customValue.dateValue);
            formattedValue = date.isValid() ? date.format('L') : '';
          }
          break;

        case 'integer':
        case 'float':
          formattedValue = String(customValue.integerValue ?? customValue.floatValue ?? '');
          break;

        default:
          formattedValue = '';
      }

      return formattedValue;
    };

    const formattedValues = customValues
      .map(formatSingleValue)
      .filter((v) => v !== '');

    return formattedValues.length > 0 ? `(${formattedValues.join(', ')})` : '';
  }

  formatStatusString(values: string[]): string {
    const labels: { [key: string]: string } = {
      draft: 'Bozza',
      new: 'Nuova',
      open: 'In Lavorazione',
      pending: 'In Attesa',
      resolved: 'Completata',
      closed: 'Chiusa',
      rejected: 'Respinta',
      cancelled: 'Annullata',
    };

    if (!values || !Array.isArray(values)) {
      return '';
    }

    const labelString = values.map((v) => labels[v] || v).join(', ');
    return labelString;
  }

  formatActivityStatusString(values: string[]): string {
    const labels: { [key: string]: string } = {
      todo: 'Da fare',
      na: 'Non applicabile',
      done: 'Fatto',
      waiting: 'In attesa',
    };

    if (!values || !Array.isArray(values)) {
      return '';
    }

    const labelString = values.map((v) => labels[v] || v).join(', ');
    return labelString;
  }

  formatCommissioningStatusString(value: string): string {
    if (!value) return '';
    if (value === 'suggested') return 'Data proposta';
    if (value === 'confirmed') return 'Data confermata';
    if (value === 'tested') return 'Collaudo confermato';
    return value;
  }
}
